import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { projectIdSelector } from '@/redux/project/project.selectors';
import { loginUserSelector } from '@/redux/user/user.selectors';
import { BASE_API_URL } from '@/constants';
import { ACKNOWLEDGE_OPERATION_TYPES } from '@/utils/websocket-entity-updates/webSocketEntityUpdates.constants';
import {
  WebSocketAcknowledgeInterface,
  WebSocketEntityUpdateInterface,
  WebSocketTextMessageInterface,
} from '@/utils/websocket-entity-updates/webSocketEntityUpdates.types';
import { getAccessToken } from '@/services/api/utils/authStorage';
import * as authStorage from '@/services/api/utils/authStorage';
import { ensureFreshAccessToken } from '@/services/api/utils/tokenAuth';
import { TOKEN_REFRESHED_EVENT } from '@/services/api/utils/authEvents';

export interface WebSocketEntityUpdatesContextInterface {
  sendMessage: (msg: string) => void;
  lastJsonMessage:
    | WebSocketEntityUpdateInterface
    | WebSocketAcknowledgeInterface
    | WebSocketTextMessageInterface
    | null;
  readyState: ReadyState;
}

const WebSocketEntityUpdatesContext = createContext<
  WebSocketEntityUpdatesContextInterface | undefined
>(undefined);
const SOCKET_URL = `${BASE_API_URL.replace('https', 'wss')}/websocket/entity-updates`;
const LOGIN_COMMAND_ID = 'login';

export const WebSocketEntityUpdatesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const projectId = useAppSelector(projectIdSelector);
  const login = useAppSelector(loginUserSelector);
  const [isLogged, setIsLogged] = useState(false);
  const { lastJsonMessage, sendMessage, readyState } = useWebSocket<
    WebSocketEntityUpdateInterface | WebSocketAcknowledgeInterface
  >(SOCKET_URL, {
    shouldReconnect: () => true,
    reconnectInterval: 5000,
    share: true,
    onMessage: (messageEvent: MessageEvent) => {
      try {
        const data = JSON.parse(messageEvent.data);
        if (
          data.commandId === LOGIN_COMMAND_ID &&
          data.type === ACKNOWLEDGE_OPERATION_TYPES.MESSAGE_PROCESSED_SUCCESSFULLY &&
          data.message === 'ok'
        ) {
          setIsLogged(true);
        }
      } catch {
        throw new Error('Websocket message parsing error');
      }
    },
  });

  // Re-authenticate the socket whenever the logged-in user changes (e.g. switching accounts),
  // since a stale `isLogged` from the previous user would otherwise block the re-login below.
  useEffect(() => {
    setIsLogged(false);
  }, [login]);

  // The socket authenticates once per connection with the access token available at that
  // moment, so a background refresh would leave the backend holding an expired token.
  // Dropping `isLogged` makes the login effect below re-authenticate with the new token.
  useEffect(() => {
    const handleTokenRefreshed = (): void => setIsLogged(false);
    window.addEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
  }, []);

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) {
      return;
    }
    if (authStorage.getAccessToken() && !isLogged) {
      // a refresh that failed for a temporary reason keeps the session - log in with the current token
      ensureFreshAccessToken()
        .catch(() => undefined)
        .then(() => {
          sendMessage(
            JSON.stringify({
              command: 'login',
              token: getAccessToken(),
              commandId: LOGIN_COMMAND_ID,
            }),
          );
        });
    } else if (!authStorage.getAccessToken()) {
      sendMessage(
        JSON.stringify({
          command: 'login',
          token: 'N/A',
          commandId: LOGIN_COMMAND_ID,
        }),
      );
      setIsLogged(false);
    }
  }, [isLogged, readyState, sendMessage, login]);

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) {
      return;
    }

    if (projectId && isLogged) {
      sendMessage(JSON.stringify({ command: 'register-for-project-updates', projectId }));
    }
  }, [projectId, readyState, sendMessage, isLogged]);

  const contextValue = useMemo(
    () => ({ sendMessage, lastJsonMessage, readyState }),
    [sendMessage, lastJsonMessage, readyState],
  );

  return (
    <WebSocketEntityUpdatesContext.Provider value={contextValue}>
      {children}
    </WebSocketEntityUpdatesContext.Provider>
  );
};

export const useWebSocketEntityUpdatesContext = (): WebSocketEntityUpdatesContextInterface => {
  const context = useContext(WebSocketEntityUpdatesContext);
  if (!context) {
    throw new Error(
      'useWebSocketEntityUpdatesContext must be used inside a WebSocketEntityUpdatesProvider',
    );
  }
  return context;
};
